import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useStoreList } from '../../context/StoreListContext';
import useGetStoreById from './useGetStoreById';

const STORAGE_KEY = 'workspace_selected_stores';

const getStoredStores = (): Record<string, string | number> => {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    } catch {
        return {};
    }
};

const saveStoreId = (workspaceId: string, storeId: string | number): void => {
    const stores = getStoredStores();
    stores[workspaceId] = storeId;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stores));
};

export const useStoreSelector = () => {
    const location = useLocation();
    const navigationStoreId = (location.state as { storeId?: string | number })?.storeId;
    
    const { stores: listStores, loading: loadingList, error: errorList, refreshStores: refetch } = useStoreList();
    const { getStoreById, loading: loadingStore, error: errorStore, data: storeData, isReady, notFound, workspaceId } = useGetStoreById();

    const [selectedStoreId, setSelectedStoreId] = useState<string | number | null>(null);
    const [entityId, setEntityId] = useState<string>('');
    const [pendingStoreId, setPendingStoreId] = useState<string | number | null>(null);
    const [isLoadingWorkspaceChange, setIsLoadingWorkspaceChange] = useState(false);
    
    const prevStoreId = useRef<string | number | null>(null);
    const prevWorkspaceId = useRef<string | null>(null);
    const isWorkspaceChanging = useRef(false);
    const isInitialLoad = useRef(true);
    const hasRefetched = useRef(false);
    const userCleared = useRef(false);

    useEffect(() => {
        if (workspaceId && prevWorkspaceId.current && prevWorkspaceId.current !== workspaceId) {
            isWorkspaceChanging.current = true;
            isInitialLoad.current = true;
            userCleared.current = false;
            setSelectedStoreId(null);
            setIsLoadingWorkspaceChange(true);
            prevStoreId.current = null;
            hasRefetched.current = false;
        }
        prevWorkspaceId.current = workspaceId;
    }, [workspaceId]);

    useEffect(() => {
        if (!workspaceId || !isWorkspaceChanging.current || selectedStoreId || !listStores?.length || loadingList) return;

        const savedStoreId = getStoredStores()[workspaceId];
        const storeExists = savedStoreId && listStores.some(s => s.ENTITY_ID.toString() === savedStoreId.toString());
        
        setSelectedStoreId(storeExists ? savedStoreId : listStores[0].ENTITY_ID);
        isWorkspaceChanging.current = false;
        setIsLoadingWorkspaceChange(false);
    }, [workspaceId, selectedStoreId, listStores, loadingList]);

    useEffect(() => {
        if (!selectedStoreId || !workspaceId || !listStores?.length || loadingList || isWorkspaceChanging.current || !isReady) return;

        const storeExists = listStores.some(s => s.ENTITY_ID.toString() === selectedStoreId.toString());
        if (!storeExists) return;

        if (isInitialLoad.current || prevStoreId.current?.toString() !== selectedStoreId.toString()) {
            saveStoreId(workspaceId, selectedStoreId);
            getStoreById({ entity_id: selectedStoreId });
            prevStoreId.current = selectedStoreId;
            isInitialLoad.current = false;
        }
    }, [selectedStoreId, workspaceId, isReady, listStores, loadingList, getStoreById]);

    useEffect(() => {
        if (!notFound || !listStores?.length || !workspaceId) return;

        toast.error("Aucun magasin avec l'id " + notFound.entityId + " n'a été trouvé");
        
        const firstStoreId = listStores[0].ENTITY_ID;
        if (firstStoreId.toString() !== notFound.entityId.toString()) {
            setSelectedStoreId(firstStoreId);
            saveStoreId(workspaceId, firstStoreId);
        }
        setEntityId('');
    }, [notFound, listStores, workspaceId]);

    useEffect(() => {
        if (!navigationStoreId || !workspaceId || hasRefetched.current) return;

        setPendingStoreId(navigationStoreId);
        saveStoreId(workspaceId, navigationStoreId);
        refetch();
        hasRefetched.current = true;
        window.history.replaceState({}, document.title);
    }, [navigationStoreId, workspaceId, refetch]);

    useEffect(() => {
        if (!pendingStoreId || !listStores?.length || loadingList) return;

        const storeExists = listStores.some(s => s.ENTITY_ID.toString() === pendingStoreId.toString());
        if (storeExists) {
            setSelectedStoreId(pendingStoreId);
            setPendingStoreId(null);
        }
    }, [pendingStoreId, listStores, loadingList]);

    useEffect(() => {
        if (selectedStoreId || !listStores?.length || !workspaceId || userCleared.current) return;

        const savedStoreId = getStoredStores()[workspaceId];
        const storeToSelect = savedStoreId && listStores.find(s => s.ENTITY_ID.toString() === savedStoreId.toString())
            ? savedStoreId
            : listStores[0]?.ENTITY_ID;

        if (storeToSelect) {
            setSelectedStoreId(storeToSelect);
            if (!savedStoreId || savedStoreId.toString() !== storeToSelect.toString()) {
                saveStoreId(workspaceId, storeToSelect);
            }
        }
    }, [selectedStoreId, listStores, workspaceId]);

    const handleStoreChange = useCallback((storeId: string | number) => {
        if (storeId === '') {
            userCleared.current = true;
            setSelectedStoreId(null);
        } else {
            userCleared.current = false;
            setSelectedStoreId(storeId);
        }
        setEntityId('');
    }, []);

    const handleEntityIdChange = useCallback((id: string) => setEntityId(id), []);
    
    const handleShowClick = useCallback(() => {
        if (entityId) setSelectedStoreId(entityId);
    }, [entityId]);

    const refetchStore = useCallback(() => {
        if (selectedStoreId && isReady) getStoreById({ entity_id: selectedStoreId });
    }, [selectedStoreId, isReady, getStoreById]);

    return {
        listStores,
        storeData,
        selectedStoreId,
        entityId,
        workspaceId,
        loadingList,
        loadingStore: loadingStore || isLoadingWorkspaceChange,
        errorList,
        errorStore,
        notFound,
        handleStoreChange,
        handleEntityIdChange,
        handleShowClick,
        refetchStore,
        setSelectedStoreId,
        setEntityId,
    };
};

export default useStoreSelector;
